import React, { useState, useCallback, useEffect } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import Toggle from './Toggle';
import { useFieldDefinitions, FieldDefinition } from '../contexts/FieldDefinitionsContext';
import { FilterCondition, FilterConditionRow } from './FilterConditionRow';
import SharedCardPickerModal from './SharedCardPickerModal';

interface PreBuiltBucket {
  id: string;
  name: string;
  description: string;
}

interface FirstCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (cardName: string, conditions: FilterCondition[], view: string, bucket?: string, statButtonLabel?: string, statButtonCountField?: string, showStatButton?: boolean, isShared?: boolean, sharedTableSettings?: any, includeClosed?: boolean, hidden?: boolean) => void;
  onDelete?: () => void;
  initialCardName?: string;
  initialConditions?: FilterCondition[];
  initialView?: string;
  isEditMode?: boolean;
  initialBucket?: string;
  initialStatButtonLabel?: string;
  initialStatButtonCountField?: string;
  initialShowStatButton?: boolean;
  hideStatButtonSettings?: boolean;
  initialIsShared?: boolean;
  allowHidden?: boolean;
  initialHidden?: boolean;
  visibleCardLimitReached?: boolean;
  /** Whether this card's filter includes closed orders (ordersall/legsall). */
  initialIncludeClosed?: boolean;
  /** Which dashboard this modal belongs to ('dispatch' | 'lc'). Scopes the "Copy Shared Card" picker. */
  dashboardType: string;
}


// Pre-built buckets for dispatch dashboard
const PRE_BUILT_BUCKETS: PreBuiltBucket[] = [
  {
    id: 'per-diem-not-dispatched',
    name: 'Per Diem Not Dispatched',
    description: 'Orders with per diem started but not yet dispatched',
  },
  {
    id: 'cutoff-not-dispatched',
    name: 'Cutoff Not Dispatched',
    description: 'Export and Rail to Port orders with cutoff datetime that are not yet dispatched',
  },
  {
    id: 'cutoff-not-terminated',
    name: 'Cutoff Not Terminated',
    description: 'Export and Rail to Port orders with cutoff datetime that are dispatched but not yet terminated',
  },
  {
    id: 'drop-released-not-dispatched',
    name: 'Drop Released Not Dispatched',
    description: 'Orders that have drop release datetime but are not yet dispatched',
  },
  {
    id: 'terminated-not-complete',
    name: 'Terminated Not Complete',
    description: 'Dispatched orders that have unassigned legs remaining',
  },
  {
    id: 'no-outgate',
    name: 'No Outgate',
    description: 'Import orders scheduled for today that have not yet outgated',
  },
  {
    id: 'past-due-no-outgate',
    name: 'Past Due No Outgate',
    description: 'Import pickups scheduled before today that are still assigned to drivers and have not outgated',
  },
  {
    id: 'past-due',
    name: 'Past Due Per Diem',
    description: 'Orders with past due per diem that need attention',
  },
  {
    id: 'live-load-unload-no-terminate',
    name: 'Live Load/Unload, No Terminate',
    description: 'Live load/unload orders with delivery leg assigned but no terminate leg',
  },
  {
    id: 'dispatched-driver-oos',
    name: 'Dispatched - Driver OOS',
    description: 'Dispatched orders where the driver is out of service',
  },
  {
    id: 'dispatches-not-accepted',
    name: 'Dispatches Not Accepted',
    description: 'Dispatched orders due today where the driver has not accepted the dispatch',
  },
  {
    id: 'scheduled-not-dispatched',
    name: 'Scheduled Not Dispatched',
    description: 'Orders that are scheduled but not yet dispatched',
  },
  {
    id: 'dispatch-started-not-completed',
    name: 'Dispatch Started, Not Completed',
    description: 'Dispatched orders that have started but not completed',
  },
  {
    id: 'dispatch-not-started',
    name: 'Dispatch Not Started',
    description: 'Dispatched orders with drivers assigned but no activity yet',
  },
  {
    id: 'trace-terminate-no-driver',
    name: 'Trace Terminate No Driver',
    description: 'Trace-terminated orders missing a driver on the terminate (or delivery, when there is no terminate leg)',
  },
  {
    id: 'pod-bol-mr-pl-eir-required',
    name: 'POD/BOL/MR/PL/EIR Required',
    description: 'Completed orders awaiting invoicing that are flagged as needing POD, BOL, MR/PL, interchange, or chassis EIR documentation',
  },
  {
    id: 'invalid-container-numbers',
    name: 'Invalid Container Numbers',
    description: 'Orders where the container number recorded on a leg document fails the ISO 6346 check (loose mode)',
  },
  {
    id: 'missing-waiting-time',
    name: 'Missing Waiting Time',
    description: 'Driver-pay waiting-time line items missing in or out times whose event date is past the last payroll cutoff',
  },
  {
    id: 'dispatched-not-yet-arrived',
    name: 'Dispatched Not Yet Arrived',
    description: "Delivery legs scheduled for today whose driver hasn't arrived yet",
  },
  {
    id: 'not-validated-chassis',
    name: 'Not Validated Chassis',
    description: 'Orders flagged with a non-validated chassis — review and mark each as Valid (Y) with a required comment, or clear the flag.',
  },
  {
    id: 'declined-cancelled-legs',
    name: 'Declined/Cancelled Legs',
    description: 'Legs a driver declined in the last 30 days that nobody has resolved — tick Resolve (optional comment) and Save to acknowledge.',
  },
  {
    id: 'declined-last-24h',
    name: 'Declined Dispatches (24h)',
    description: 'Every dispatch a driver declined in the last 24 hours, resolved or not, newest first — the Declined Dispatches report columns.',
  },
  {
    id: 'lfd-today-or-past',
    name: 'LFD Today or Past Due',
    description: 'Containers still at the terminal (no outgate) whose last free day is today or already past, most overdue first.',
  },
];

export default function FirstCardModal({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialCardName,
  initialConditions,
  initialView,
  isEditMode = false,
  initialBucket,
  initialStatButtonLabel,
  initialStatButtonCountField,
  initialShowStatButton,
  hideStatButtonSettings = false,
  initialIsShared,
  allowHidden = false,
  initialHidden = false,
  visibleCardLimitReached = false,
  initialIncludeClosed,
  dashboardType,
}: FirstCardModalProps) {
  const [cardName, setCardName] = useState(initialCardName || '');
  const [cardNameError, setCardNameError] = useState(false);
  const [filterError, setFilterError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    if (initialConditions && initialConditions.length > 0) {
      return initialConditions.map((c, i) => ({ ...c, id: i }));
    }
    return [{ id: 0, field: '', compare: '', values: '' }];
  });
  const [conditionIdCounter, setConditionIdCounter] = useState(() => {
    return initialConditions && initialConditions.length > 0 ? initialConditions.length : 1;
  });
  const [currentView] = useState(initialView || 'Orders');
  const [statButtonLabel, setStatButtonLabel] = useState(initialStatButtonLabel || '');
  const [statButtonCountField, setStatButtonCountField] = useState(initialStatButtonCountField || '');
  const [showStatButton, setShowStatButton] = useState(initialShowStatButton ?? true);
  const [hidden, setHidden] = useState(initialHidden);
  const [limitError, setLimitError] = useState(false);
  const [isShared, setIsShared] = useState(initialIsShared || false);
  const [showSharedCardPicker, setShowSharedCardPicker] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<string | undefined>(initialBucket);
  const [includeClosed, setIncludeClosed] = useState(initialIncludeClosed || false);
  const [sharedTableSettings, setSharedTableSettings] = useState<any>(undefined);

  // Get field definitions from context (cached)
  const { fieldDefinitions, loading: loadingFields } = useFieldDefinitions();

  const addCondition = useCallback(() => {
    setConditions((prev) => [
      ...prev,
      { id: conditionIdCounter, field: '', compare: '', values: '' },
    ]);
    setConditionIdCounter((prev) => prev + 1);
  }, [conditionIdCounter]);

  const removeCondition = useCallback((id: number) => {
    setConditions((prev) => {
      if (prev.length > 1) {
        return prev.filter((c) => c.id !== id);
      }
      return prev;
    });
  }, []);

  const updateCondition = useCallback(
    (id: number, field: keyof FilterCondition, value: string) => {
      setConditions((prev) =>
        prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
      );
      if (filterError) setFilterError(false);
    },
    [filterError]
  );

  const resetFilters = useCallback(() => {
    setConditions([{ id: conditionIdCounter, field: '', compare: '', values: '' }]);
    setConditionIdCounter((prev) => prev + 1);
  }, [conditionIdCounter]);

  /**
   * Initialize form state when modal opens with initial values from props.
   * Note: This useEffect is intentional for edit modals that need to populate
   * internal form state from props each time they open. The form state must be
   * independent for user edits, but needs to sync from props on open.
   * A key prop alternative would require parent coordination for modal state.
   */
  useEffect(() => {
    if (isOpen) {
      // When opening, set initial values
      setCardName(initialCardName || '');
      setCardNameError(false);
      setFilterError(false);
      if (initialConditions && initialConditions.length > 0) {
        setConditions(initialConditions.map((c, i) => ({ ...c, id: i })));
        setConditionIdCounter(initialConditions.length);
      } else {
        setConditions([{ id: 0, field: '', compare: '', values: '' }]);
        setConditionIdCounter(1);
      }
      setStatButtonLabel(initialStatButtonLabel || '');
      setStatButtonCountField(initialStatButtonCountField || '');
      setShowStatButton(initialShowStatButton ?? true);
      setIsShared(initialIsShared || false);
      setHidden(initialHidden);
      setLimitError(false);
      setIncludeClosed(initialIncludeClosed || false);
      setSelectedBucket(initialBucket);
    }
  }, [isOpen, initialCardName, initialConditions, initialStatButtonLabel, initialStatButtonCountField, initialShowStatButton, initialIsShared, initialIncludeClosed, initialBucket, initialHidden]);

  // Alt+C closes. No Alt+S: the card saves through a native form submit, so
  // Enter already covers it. The layer keeps Alt+S off the dashboard behind.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const mainDrag = useModalDrag(isOpen);
  const confirmDrag = useModalDrag(showDeleteConfirm);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div
        ref={mainDrag.panelRef}
        style={mainDrag.panelStyle}
        className="bg-panel rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
      >
        <ModalHeader
          title="Card Settings"
          onClose={onClose}
          dragHandleProps={mainDrag.dragHandleProps}
          actions={
            <button
              onClick={resetFilters}
              className="text-slate-300 hover:text-white transition-colors p-1"
              title="Reset"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />
        {/* Card name row */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-label">Card Name:</label>
            <div className="relative">
              <input
                type="text"
                value={cardName}
                onChange={(e) => {
                  setCardName(e.target.value);
                  if (cardNameError) setCardNameError(false);
                }}
                className={`px-2 py-1 text-sm border ${cardNameError ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 dark:border-gray-600'} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white`}
                placeholder="Card Name..."
              />
              {cardNameError && (
                <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 text-red-500 text-xs font-medium whitespace-nowrap">
                  Enter A Card Name
                </span>
              )}
            </div>
            {(isEditMode || allowHidden) && (
              <div className="flex items-center gap-2 ml-3">
                <label className="text-sm text-gray-600 dark:text-gray-400">Shared</label>
                <Toggle
                  checked={isShared}
                  onChange={(checked) => setIsShared(checked)}
                  size="sm"
                  ariaLabel="Share this card"
                />
              </div>
            )}
            {allowHidden && <div className="flex items-center gap-2 ml-3">
              <label className="text-sm text-gray-600 dark:text-gray-400">Hidden</label>
              <Toggle checked={hidden} onChange={checked => { setHidden(checked); setLimitError(false); }} size="sm" ariaLabel="Hide this card" />
            </div>}
          </div>
          {limitError && <p role="alert" className="mt-2 text-sm text-red-600">This workspace already has 10 visible cards. Keep this card hidden or hide another card first.</p>}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Bucket Information Banner */}
          {selectedBucket && (() => {
            const bucketInfo = PRE_BUILT_BUCKETS.find(b => b.id === selectedBucket);
            return bucketInfo ? (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Based on: {bucketInfo.name}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      {bucketInfo.description}
                    </p>
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          {/* Filter Conditions */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-label">
                {initialBucket ? 'Additional Filter Conditions (optional)' : 'Filter Conditions'}
              </label>
              {filterError && (
                <span className="text-red-500 text-xs font-medium">At least one filter is required</span>
              )}
            </div>
            {conditions.map((condition) => (
              <FilterConditionRow
                key={condition.id}
                condition={condition}
                fieldDefinitions={fieldDefinitions}
                loadingFields={loadingFields}
                onUpdate={updateCondition}
                onRemove={removeCondition}
                removeDisabled={conditions.length === 1}
              />
            ))}
            {/* Full-width Add Filter under the last condition so users can keep
                adding rows without moving the cursor back up to the header. */}
            <button
              onClick={addCondition}
              type="button"
              className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border border-dashed border-indigo-300 dark:border-indigo-700 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Filter
            </button>

            {/* Scope toggle. Custom-filter cards only: a pre-built bucket declares its
                own table_scope in the backend registry, and bucket cards are served by
                /batch-cards, which does not read include_closed — so showing this on a
                bucket card would be a control that does nothing. */}
            {!selectedBucket && (
              <label className="flex items-center gap-1.5 pt-1 text-[12px] font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
                <Toggle
                  size="sm"
                  checked={includeClosed}
                  onChange={setIncludeClosed}
                  ariaLabel="Include closed orders"
                />
                Include Closed Orders
                <span className="font-normal text-slate-400 dark:text-slate-500">
                  — searches closed orders too; results are capped and closed rows are highlighted
                </span>
              </label>
            )}
          </div>

          {/* StatButton Settings - Only shown on main workspace */}
          {!hideStatButtonSettings && (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-rule">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-label">
                  Stat Button Settings
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setShowStatButton(!showStatButton);
                    if (showStatButton) {
                      setStatButtonLabel('');
                      setStatButtonCountField('');
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showStatButton ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showStatButton ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {showStatButton && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-muted mb-1">
                      Button Label (optional)
                    </label>
                    <input
                      type="text"
                      value={statButtonLabel}
                      onChange={(e) => setStatButtonLabel(e.target.value)}
                      placeholder="Defaults to card name"
                      className="w-full px-2 py-1.5 border border-slate-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted mb-1">
                      Count Field
                    </label>
                    <select
                      value={statButtonCountField}
                      onChange={(e) => setStatButtonCountField(e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-panel dark:text-white"
                      disabled={loadingFields}
                    >
                      <option value="">All rows (no date filter)</option>
                      <option value="TODAY_ALL">Today - All Rows</option>
                      {fieldDefinitions
                        .filter(def => def.type === 'date' || def.type === 'datetime' || def.fieldname.toLowerCase().includes('date'))
                        .map((def) => (
                          <option key={def.fieldname} value={def.fieldname}>
                            Today - {def.displayname}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Choose which date field to use for "Today" count
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - Action Buttons */}
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
          <div>
            {isEditMode && onDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Delete Card
              </button>
            )}
            {!isEditMode && (
              <button
                onClick={() => setShowSharedCardPicker(true)}
                type="button"
                className="px-4 py-2 bg-white dark:bg-gray-700 text-label border border-gray-300 dark:border-gray-600 rounded text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Copy Shared Card
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-label rounded text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!cardName.trim()) {
                  setCardNameError(true);
                  return;
                }
                // Filter out empty conditions before submitting
                const validConditions = conditions.filter(c => c.field && c.field.trim());
                // Require at least one filter unless card is bucket-based
                if (validConditions.length === 0 && !selectedBucket) {
                  setFilterError(true);
                  return;
                }
                if (allowHidden && !hidden && visibleCardLimitReached) {
                  setLimitError(true);
                  return;
                }
                // Preserve the bucket if it exists (important for duplicated/shared bucket-based cards)
                onSubmit(
                  cardName.trim(),
                  validConditions,
                  currentView,
                  selectedBucket,
                  showStatButton ? (statButtonLabel.trim() || undefined) : undefined,
                  showStatButton ? (statButtonCountField || undefined) : undefined,
                  showStatButton,
                  isShared,
                  sharedTableSettings,
                  selectedBucket ? false : includeClosed,
                  allowHidden ? hidden : false
                );
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Save Card Settings
            </button>
          </div>
        </div>

      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-scrim bg-opacity-50">
          <div
            ref={confirmDrag.panelRef}
            style={confirmDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <ModalHeader
              title="Delete Card?"
              onClose={() => setShowDeleteConfirm(false)}
              dragHandleProps={confirmDrag.dragHandleProps}
            />
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete this card? It will be moved to the trashcan for 30 days.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-label rounded text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onDelete?.();
                    setShowDeleteConfirm(false);
                    onClose();
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SharedCardPickerModal
        isOpen={showSharedCardPicker}
        dashboardType={dashboardType}
        onClose={() => setShowSharedCardPicker(false)}
        onSelect={(card) => {
          // A card copied from the *other* dashboard only carries its title and
          // filter conditions. Its bucket (dashboard-specific) and column layout
          // (partly dashboard-specific) don't transfer — the backend already
          // excludes bucket cards from cross-dashboard listings, and we drop the
          // layout here so the new card falls back to this dashboard's defaults.
          const isCrossDashboard = !!card.source_dashboard && card.source_dashboard !== dashboardType;
          setCardName(card.card_title);
          setHidden(false);
          if (card.filter_conditions && card.filter_conditions.length > 0) {
            setConditions(card.filter_conditions.map((c: any, i: number) => ({ ...c, id: i })));
            setConditionIdCounter(card.filter_conditions.length);
          }
          if (card.bucket && !isCrossDashboard) {
            setSelectedBucket(card.bucket);
          }
          if (card.table_settings && !isCrossDashboard) {
            setSharedTableSettings(card.table_settings);
          }
          setShowSharedCardPicker(false);
        }}
      />
    </div>
  );
}
